import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Eye, EyeOff, Info } from 'lucide-react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loading } from '@/components/OpsUI';
import { FollowupTable } from './FollowupTable';
import { FollowupResendButton } from './FollowupResendButton';

const GROUPS = [
  ['read_no_reply', 'Lo leyeron y no contestaron', Eye, 'text-sky-300'],
  ['delivered_unread', 'Lo recibieron y no lo abrieron', EyeOff, 'text-amber-300'],
];

export function FollowupPanel({ campaignId, campaigns, canAct }) {
  const [data, setData] = useState(null);
  const [group, setGroup] = useState('read_no_reply');
  const [camp, setCamp] = useState('all');
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState([]);
  const cid = campaignId || (camp === 'all' ? '' : camp);

  const load = useCallback(() => {
    api.get('/wa-followup', { params: cid ? { campaign_id: cid } : {} })
      .then(({ data: d }) => setData(d)).catch(() => setData({ items: [], counts: {}, sending: 0 }));
  }, [cid]);
  useEffect(() => { load(); setSelected([]); }, [load]);
  useEffect(() => {
    if (!data?.sending) return undefined;
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [data?.sending, load]);

  const rows = useMemo(() => (data?.items || []).filter((r) => r.group === group
    && `${r.pastor} ${r.church} ${r.phone} ${r.campaign_name}`.toLowerCase().includes(q.toLowerCase())), [data, group, q]);
  const pick = (g) => { setGroup(g); setSelected([]); };

  if (!data) return <Loading />;
  return (
    <div className="space-y-4" data-testid="followup-panel">
      <div className="grid sm:grid-cols-2 gap-4">
        {GROUPS.map(([k, label, Icon, color]) => (
          <Card key={k} onClick={() => pick(k)} data-testid={`followup-group-${k}`}
            className={`p-4 cursor-pointer transition-colors duration-150 ${group === k ? 'border-[hsl(var(--primary))]' : 'hover:border-[hsl(var(--primary)/.5)]'}`}>
            <div className="text-xs text-[hsl(var(--muted-foreground))] flex items-center gap-1"><Icon className={`h-3.5 w-3.5 ${color}`} />{label}</div>
            <div className="text-2xl font-bold mt-1" data-testid={`followup-count-${k}`}>{data.counts?.[k] ?? 0}</div>
          </Card>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input placeholder="Buscar pastor, iglesia, teléfono..." value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="followup-search" />
        {!campaignId && (
          <Select value={camp} onValueChange={setCamp}>
            <SelectTrigger className="w-60" data-testid="followup-campaign-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los despliegues</SelectItem>
              {(campaigns || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {canAct && <div className="ml-auto"><FollowupResendButton ids={selected} onDone={() => { setSelected([]); load(); }} /></div>}
      </div>
      {data.sending > 0 && <div className="text-xs text-amber-300" data-testid="followup-sending-indicator">Reenviando {data.sending} {data.sending === 1 ? 'mensaje' : 'mensajes'}… se actualiza solo.</div>}
      <FollowupTable rows={rows} showCampaign={!campaignId} canAct={canAct} selected={selected} setSelected={setSelected} />
      <p className="text-xs text-[hsl(var(--muted-foreground))] flex gap-1" data-testid="followup-note">
        <Info className="h-3.5 w-3.5 shrink-0 mt-0.5" />
        Solo cuenta la bienvenida por WhatsApp. Si un pastor tiene desactivada la confirmación de lectura, aparece como “recibido, sin abrir” aunque lo haya leído. Quienes contestan salen de este reporte automáticamente.
      </p>
    </div>
  );
}
